import { initializeConnector } from '@web3-react/core';
import { MetaMask } from '@web3-react/metamask';
import { WalletConnect } from '@web3-react/walletconnect';
import { CoinbaseWallet } from '@web3-react/coinbase-wallet';
import { errorLogger } from '../utils/errorLogger';
import {
  SUPPORTED_NETWORKS,
  getNetworkConfig,
  BSC_NETWORK_ID,
  DEFAULT_NETWORK_ID,
  getNetworkDisplayName,
  SOLANA_NETWORK_ID
} from '../config/networks';
import { networkService } from '../services/networkService';

// Re-export Solana types for convenience
export { useSolanaWallet, SolanaWalletProvider, type SolanaWalletName } from './solanaContext';

// Type for enhanced ethereum provider
interface EnhancedEthereumProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isConnected?: () => boolean;
  on?: (event: string, handler: (data: any) => void) => void;
  removeListener?: (event: string, handler: (data: any) => void) => void;
}

// Multi-chain RPC URLs using the network configuration
const RPC_URLS: { [chainId: number]: string } = {
  1: 'https://cloudflare-eth.com', // Ethereum mainnet
  56: 'https://bsc-dataseed1.binance.org/', // BSC mainnet
  97: 'https://data-seed-prebsc-1-s1.binance.org:8545/', // BSC testnet
  11155111: 'https://rpc.sepolia.org' // Sepolia testnet
};

// Get all supported chain IDs for connectors - reserved for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _getSupportedChainIds = (): number[] => {
  return Object.keys(SUPPORTED_NETWORKS).map(Number);
};

// Build RPC configuration for all supported networks
const buildRpcConfig = (): { [chainId: number]: string } => {
  const rpcConfig: { [chainId: number]: string } = {};
  
  Object.entries(SUPPORTED_NETWORKS).forEach(([chainId, network]) => {
    rpcConfig[parseInt(chainId)] = network.rpcUrls[0];
  });
  
  return rpcConfig;
};

// Error types for wallet connections
export enum WalletConnectionError {
  WALLET_NOT_INSTALLED = 'WALLET_NOT_INSTALLED',
  USER_REJECTED = 'USER_REJECTED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_NETWORK = 'INVALID_NETWORK',
  CONNECTION_TIMEOUT = 'CONNECTION_TIMEOUT',
  PROVIDER_ERROR = 'PROVIDER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface ConnectionResult {
  success: boolean;
  account?: string;
  error?: {
    type: WalletConnectionError;
    message: string;
    originalError?: any;
  };
}

// Enhanced error handling utilities
export const handleConnectionError = (error: any, walletName: string): ConnectionResult['error'] => {
  let errorType: WalletConnectionError;
  let userMessage: string;
  
  if (error?.code === 4001) {
    errorType = WalletConnectionError.USER_REJECTED;
    userMessage = 'Connection request was rejected. Please try again and approve the connection.';
  } else if (error?.code === -32002) {
    errorType = WalletConnectionError.CONNECTION_TIMEOUT;
    userMessage = 'Connection request is already pending. Please check your wallet.';
  } else if (error?.message?.includes('network') || error?.code === -32603) {
    errorType = WalletConnectionError.NETWORK_ERROR;
    userMessage = 'Network connection failed. Please check your internet connection and try again.';
  } else if (error?.message?.includes('unsupported') || error?.code === 4902) {
    errorType = WalletConnectionError.INVALID_NETWORK;
    userMessage = 'Unsupported network. Please switch to a supported network.';
  } else {
    errorType = WalletConnectionError.UNKNOWN_ERROR;
    userMessage = `Failed to connect to ${walletName}. Please try again or contact support.`;
  }

  // Log the error
  errorLogger.logWalletError(walletName, error, userMessage, true);
  
  return {
    type: errorType,
    message: userMessage,
    originalError: error
  };
};

// Enhanced wallet detection
export const isWalletInstalled = (walletName: string): boolean => {
  if (typeof window === 'undefined') return false;
  
  const win = window as any;
  
  switch (walletName) {
    case 'MetaMask':
      return typeof win.ethereum !== 'undefined' &&
             win.ethereum?.isMetaMask === true;
    case 'Coinbase Wallet':
      return typeof win.ethereum !== 'undefined' &&
             win.ethereum?.isCoinbaseWallet === true;
    case 'OKX Wallet':
      return typeof win.okxwallet !== 'undefined';
    case 'Bitget Wallet':
      return typeof win.bitget !== 'undefined';
    // Solana Wallets
    case 'Phantom':
      return !!win.phantom?.solana?.isPhantom;
    case 'Solflare':
      return !!win.solflare?.isSolflare;
    case 'MetaMask Snap':
      // MetaMask Snap requires MetaMask to be installed
      return !!win.ethereum?.isMetaMask;
    default:
      return true; // For WalletConnect and others that don't require installation
  }
};

// Helper to detect if we're on a Solana-compatible network
export const isSolanaNetwork = (chainId?: number): boolean => {
  return chainId === SOLANA_NETWORK_ID;
};

// Wallet type detection
export type WalletType = 'evm' | 'solana' | 'universal';

export const getWalletType = (walletName: string): WalletType => {
  switch (walletName) {
    case 'Phantom':
    case 'Solflare':
      return 'solana';
    case 'MetaMask Snap':
      return 'solana'; // MetaMask with Solana Snap
    case 'MetaMask':
    case 'Coinbase Wallet':
    case 'OKX Wallet':
    case 'Bitget Wallet':
      return 'evm';
    case 'WalletConnect':
    case 'Safe':
      return 'universal';
    default:
      return 'evm';
  }
};

// Enhanced network switching helper with BSC support
export const switchToNetwork = async (chainId: number): Promise<ConnectionResult> => {
  try {
    if (typeof window === 'undefined' || !window.ethereum) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.WALLET_NOT_INSTALLED,
          message: 'No wallet detected. Please install a compatible wallet.'
        }
      };
    }

    // Use the network service for enhanced network switching
    const result = await networkService.switchToNetwork(chainId);
    
    if (result.success) {
      return { success: true };
    } else {
      return {
        success: false,
        error: {
          type: result.error?.code === 4902 ? WalletConnectionError.INVALID_NETWORK : WalletConnectionError.NETWORK_ERROR,
          message: result.error?.message || `Failed to switch to ${getNetworkDisplayName(chainId)}`,
          originalError: result.error
        }
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: handleConnectionError(error, 'Network Switch')
    };
  }
};

// Helper function to add BSC network to wallet
export const addBSCNetwork = async (): Promise<ConnectionResult> => {
  return await addNetworkToWallet(BSC_NETWORK_ID);
};

// Helper function to add any supported network to wallet
export const addNetworkToWallet = async (chainId: number): Promise<ConnectionResult> => {
  try {
    if (typeof window === 'undefined' || !window.ethereum) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.WALLET_NOT_INSTALLED,
          message: 'No wallet detected. Please install a compatible wallet.'
        }
      };
    }

    const networkConfig = getNetworkConfig(chainId);
    if (!networkConfig) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.INVALID_NETWORK,
          message: `Unsupported network: ${chainId}`
        }
      };
    }

    // Use network service to add and switch to the network
    const result = await networkService.addAndSwitchToNetwork(chainId);
    
    if (result.success) {
      return { success: true };
    } else {
      return {
        success: false,
        error: {
          type: WalletConnectionError.NETWORK_ERROR,
          message: result.error?.message || `Failed to add ${networkConfig.displayName}`,
          originalError: result.error
        }
      };
    }
  } catch (error: any) {
    return {
      success: false,
      error: handleConnectionError(error, 'Add Network')
    };
  }
};

export const [metaMask, metaMaskHooks] = initializeConnector<MetaMask>((actions) => new MetaMask({ actions }));

export const [walletConnect, walletConnectHooks] = initializeConnector<WalletConnect>((actions) => new WalletConnect({
  actions,
  options: {
    rpc: buildRpcConfig(),
    qrcode: true
  }
}));

export const [coinbaseWallet, coinbaseWalletHooks] = initializeConnector<CoinbaseWallet>((actions) => new CoinbaseWallet({
  actions,
  options: {
    url: RPC_URLS[DEFAULT_NETWORK_ID],
    appName: 'E-Payment Stablecoin'
  }
}));

export const connectors: [any, any][] = [
  [metaMask, metaMaskHooks],
  [walletConnect, walletConnectHooks],
  [coinbaseWallet, coinbaseWalletHooks]
];

export const getConnector = (walletName: string) => {
  switch (walletName) {
    case 'MetaMask':
      return metaMask;
    case 'Coinbase Wallet':
      return coinbaseWallet;
    case 'WalletConnect':
    case 'Bitget Wallet':
    case 'OKX Wallet':
    case 'Safe':
    case 'TronLink Wallet':
    case 'Slush Wallet':
      return walletConnect; // Use WalletConnect for these wallets
    default:
      return metaMask; // Default to MetaMask
  }
};

// Enhanced connection function with comprehensive error handling
export const connectWallet = async (walletName: string): Promise<ConnectionResult> => {
  try {
    // Check if wallet is installed
    if (!isWalletInstalled(walletName)) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.WALLET_NOT_INSTALLED,
          message: `${walletName} is not installed. Please install the wallet extension and try again.`
        }
      };
    }

    const connector = getConnector(walletName);
    
    // Set connection timeout
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 15000); // 15 second timeout
    });

    // Attempt connection with timeout
    const connectionPromise = connector.activate();
    
    await Promise.race([connectionPromise, timeoutPromise]);
    
    // Get account information
    if (typeof window === 'undefined' || !window.ethereum) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.WALLET_NOT_INSTALLED,
          message: 'Wallet provider not available.'
        }
      };
    }

    const accounts = await (window.ethereum as EnhancedEthereumProvider).request({ method: 'eth_accounts' });
    
    if (!accounts || accounts.length === 0) {
      return {
        success: false,
        error: {
          type: WalletConnectionError.USER_REJECTED,
          message: 'No accounts found. Please make sure your wallet is unlocked and has at least one account.'
        }
      };
    }

    return {
      success: true,
      account: accounts[0]
    };
    
  } catch (error: any) {
    if (error.message === 'Connection timeout') {
      return {
        success: false,
        error: {
          type: WalletConnectionError.CONNECTION_TIMEOUT,
          message: 'Connection timed out. Please check your wallet and try again.',
          originalError: error
        }
      };
    }
    
    return {
      success: false,
      error: handleConnectionError(error, walletName)
    };
  }
};